import { useMe } from "@/api/services/main/auth";
import { useMyOrganization } from "@/api/services/main/organization";
import { useMyProductContracts } from "@/api/services/main/product-contract";
import { initUserApi } from "@/api/services/main/user";
import { Button } from "@/components/elements/Button";
import { Input } from "@/components/elements/Input";
import { LoadingArea } from "@/components/elements/Loading/Loading";
import { Modal } from "@/components/elements/Modal";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { AppRoutes } from "@/routes/config";
import { text } from "@/theme/colors";
import { FCC } from "@/types/common";
import { showError } from "@/utils/error";
import { createUrlWithParams, openNewTab } from "@/utils/url";
import { Box, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useEffect, useRef, useState } from "react";

const MIN_DISPLAY_MS = 5000;

type TFirstLoginModalProps = {
  isOpen: boolean;
  productContractId: number | string;
  onClose: () => void;
};

export const FirstLoginModal: FC<TFirstLoginModalProps> = ({ isOpen, productContractId, onClose }) => {
  const { data: contracts, isLoading: isLoadingContracts } = useMyProductContracts();
  const { data: organization } = useMyOrganization();
  const { data: me } = useMe();
  const queryClient = useQueryClient();

  const [isProcessing, setIsProcessing] = useState(false);

  // Guards the auto-run in the effect so it fires at most once per open. Without this, invalidating
  // the product-contract query below re-creates `contract` and would re-trigger the effect, opening a new tab each time.
  const hasAutoRunRef = useRef(false);

  const contract = contracts.find((item) => String(item.id) === String(productContractId));

  // Provision the product account first; only once that succeeds is the shared per-product tab sent to
  // the SSO redirect page, which mints the (60s, single-use) redirect right before navigating.
  const handleSetup = async (withDelay = false) => {
    if (!contract) return;
    const productCode = contract.product.code;
    try {
      setIsProcessing(true);
      const delayTime = withDelay ? MIN_DISPLAY_MS : 0;
      await Promise.all([initUserApi({ productCode }), new Promise((resolve) => setTimeout(resolve, delayTime))]);

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PRODUCT_CONTRACT.FETCH_LIST] }),
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.USER.FETCH_ME] }),
      ]);

      onClose();
      openNewTab(createUrlWithParams(AppRoutes.mintOauthRedirect, { productCode }), `product:${productCode}`);
    } catch (error) {
      showError(error);
    } finally {
      setIsProcessing(false);
    }
  };

  useEffect(() => {
    // Reset the guard whenever the modal closes so re-opening runs the setup again.
    if (!isOpen) {
      hasAutoRunRef.current = false;
      return;
    }
    // Run once per open, only after the contract has loaded. The ref (not `isProcessing`) is the guard:
    // it survives the re-render caused by invalidateQueries, so we never open more than one tab.
    if (productContractId && contract && !hasAutoRunRef.current) {
      hasAutoRunRef.current = true;
      handleSetup(true);
    }
  }, [contract, isOpen, productContractId]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      enableFooter={false}
      width={660}
      title={
        <Typography variant="head20Bold" color={text.primary} width="100%" textAlign="center">
          初回ログインの処理中..
        </Typography>
      }
    >
      {!contract || !me || !organization || isLoadingContracts ? (
        <LoadingArea />
      ) : (
        <Stack gap="32px" pt="8px">
          <Box display="grid" gridTemplateColumns="1fr 1fr" rowGap="16px" columnGap="24px">
            <FieldItem label="組織ID">
              <Input nonControl readonly size="lg" value={organization.data.externalId ?? ""} />
            </FieldItem>
            <FieldItem label="組織名">
              <Input nonControl readonly size="lg" value={organization.data.name ?? ""} />
            </FieldItem>
            <Box gridColumn="1 / -1">
              <FieldItem label="対象プロダクト">
                <Input nonControl readonly size="lg" value={contract.product?.name ?? ""} />
              </FieldItem>
            </Box>
            <FieldItem label="氏名">
              <Input nonControl readonly size="lg" value={me.data.fullName ?? ""} />
            </FieldItem>
            <FieldItem label="メールアドレス">
              <Input nonControl readonly size="lg" value={me.data.email ?? ""} />
            </FieldItem>
          </Box>
          <Typography variant="body14" color={text.secondary} pt="16px" component="div">
            ユーザーを作成しています。自動で遷移しない場合は、以下からログインしてください。
          </Typography>

          <Stack gap={2} pt="32px">
            <Button
              variant="secondary"
              fullWidth
              isLoading={isProcessing}
              disabled={!contract || isProcessing}
              onClick={() => handleSetup(false)}
              sx={{ height: "48px !important" }}
            >
              <Typography variant="body14Semi">{contract.product?.name} へ移動</Typography>
            </Button>
            <Button variant="ghost" fullWidth onClick={onClose} sx={{ height: "48px !important" }}>
              <Typography variant="body14Semi">ホームへ戻る</Typography>
            </Button>
          </Stack>
        </Stack>
      )}
    </Modal>
  );
};

const FieldItem: FCC<{ label: string }> = ({ label, children }) => (
  <Stack gap="4px">
    <Typography variant="cap12" color={text.primary}>
      {label}
    </Typography>
    {children}
  </Stack>
);
